// One shared <audio> element for all playback. A media element (not Web Audio) is used because
// iOS plays it even with the ringer switch on silent, and it can slow speech down without
// changing its pitch. Reusing one element keeps it unlocked after the first tap on iOS.
//
// Clips are fetched whole and played from memory (blob URLs). Safari asks servers for media in
// byte ranges and refuses a plain full response; some static hosts, Cloudflare Pages among them,
// ignore ranges. Playing from memory works on any host, offline, and starts instantly.

export interface PlayOptions {
  rate?: number;
  /** Play only this part of the clip, in seconds. */
  from?: number;
  to?: number;
  /** Who is listening for progress; shown as the playhead. */
  tag?: string;
}

/** 'blocked' means the browser refused to start sound without a tap. */
export type PlayResult = 'ended' | 'stopped' | 'error' | 'blocked';

export interface PlaybackState {
  tag: string | null;
  /** 0–1 through the played part. */
  progress: number;
  playing: boolean;
}

type Listener = (s: PlaybackState) => void;

/** 50 ms of silence, played once from a tap so iOS allows later sounds that don't come from a tap. */
const SILENCE = 'data:audio/wav;base64,UklGRoQJAABXQVZFZm10IBAAAAABAAEAwF0AAIC7AAACABAAZGF0YWAJAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=';
type PitchElement = HTMLAudioElement & { preservesPitch?: boolean; webkitPreservesPitch?: boolean };

/** The clips in memory, as blob URLs, keyed by their path. */
class AudioBank {
  private urls = new Map<string, string>();
  private pending = new Map<string, Promise<string>>();

  /** A playable URL right now, or null if the clip still has to be fetched. */
  ready(src: string): string | null {
    if (src.startsWith('blob:') || src.startsWith('data:')) return src;
    return this.urls.get(src) ?? null;
  }

  load(src: string): Promise<string> {
    const ready = this.ready(src);
    if (ready) return Promise.resolve(ready);
    let pending = this.pending.get(src);
    if (!pending) {
      pending = fetch(src)
        .then((r) => {
          if (!r.ok) throw new Error(`${r.status} for ${src}`);
          return r.blob();
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          this.urls.set(src, url);
          return url;
        })
        .finally(() => this.pending.delete(src));
      this.pending.set(src, pending);
    }
    return pending;
  }
}

const bank = new AudioBank();

class Player {
  private el: PitchElement | null = null;
  private listeners = new Set<Listener>();
  private state: PlaybackState = { tag: null, progress: 0, playing: false };
  private finish: ((result: PlayResult) => void) | null = null;
  private raf = 0;
  private range = { from: 0, to: 0 };
  /** Bumped by every outside play or stop, so a running sequence knows it was replaced. */
  private epoch = 0;

  private element(): PitchElement {
    if (!this.el) {
      const el = new Audio() as PitchElement;
      el.preload = 'auto';
      el.setAttribute('playsinline', '');
      el.addEventListener('ended', () => this.settle('ended'));
      el.addEventListener('error', () => this.settle('error'));
      this.el = el;
    }
    return this.el;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  get current(): PlaybackState {
    return this.state;
  }

  private emit(next: PlaybackState) {
    this.state = next;
    for (const l of this.listeners) l(next);
  }

  private settle(result: PlayResult) {
    cancelAnimationFrame(this.raf);
    const finish = this.finish;
    if (!finish) return;
    this.finish = null;
    this.emit({ tag: this.state.tag, progress: result === 'ended' ? 1 : 0, playing: false });
    finish(result);
  }

  private tick = () => {
    const el = this.el;
    if (!el || !this.finish) return;
    const { from, to } = this.range;
    const end = to > from ? to : Number.isFinite(el.duration) ? el.duration : 0;
    if (end > from) {
      const p = Math.min(1, Math.max(0, (el.currentTime - from) / (end - from)));
      this.emit({ tag: this.state.tag, progress: p, playing: true });
      if (to > from && el.currentTime >= to) {
        el.pause();
        this.settle('ended');
        return;
      }
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  private halt() {
    if (!this.finish) return;
    this.el?.pause();
    this.settle('stopped');
  }

  /**
   * Plays a clip; resolves when it ends, is stopped, or fails. Call it straight from a tap
   * handler the first time, so iOS lets the page play sound.
   */
  play(src: string, options: PlayOptions = {}, inSequence = false): Promise<PlayResult> {
    if (!inSequence) this.epoch++;
    this.halt();
    const el = this.element();
    return new Promise<PlayResult>((resolve) => {
      this.finish = resolve;
      this.range = { from: options.from ?? 0, to: options.to ?? 0 };
      this.emit({ tag: options.tag ?? src, progress: 0, playing: true });
      const ready = bank.ready(src);
      if (ready) {
        this.start(el, ready, options, resolve);
        return;
      }
      // Not in memory yet: fetch it, then play if nothing else has started meanwhile. The element
      // was unlocked by an earlier tap (prime), so iOS still lets it play after the wait.
      bank
        .load(src)
        .then((url) => {
          if (this.finish === resolve) this.start(el, url, options, resolve);
        })
        .catch(() => {
          if (this.finish === resolve) this.settle('error');
        });
    });
  }

  private start(el: PitchElement, url: string, options: PlayOptions, resolve: (r: PlayResult) => void) {
    // Load the clip afresh even when it is the one already in the element. Replaying by seeking
    // back lets Safari start the sound before the seek lands, and the first syllable is lost
    // («Καλημέρα» heard as «...λημέρα»); a fresh load from memory is instant and always waits.
    el.src = url;
    el.preservesPitch = true;
    el.webkitPreservesPitch = true;
    el.defaultPlaybackRate = options.rate ?? 1;
    el.playbackRate = options.rate ?? 1;
    try {
      el.currentTime = this.range.from;
    } catch {
      // Not seekable yet: it plays from the start.
    }
    const started = el.play();
    this.raf = requestAnimationFrame(this.tick);
    // A rejection that arrives late (after another clip started) must not settle the new one.
    started?.catch((err: DOMException) => {
      if (this.finish !== resolve) return;
      this.settle(err?.name === 'AbortError' ? 'stopped' : err?.name === 'NotAllowedError' ? 'blocked' : 'error');
    });
  }

  /** Plays clips one after another. Stops early if a clip fails or anything else plays. */
  async sequence(items: Array<{ src: string } & PlayOptions>): Promise<PlayResult> {
    const epoch = ++this.epoch;
    for (const item of items) {
      const result = await this.play(item.src, item, true);
      if (result !== 'ended') return result;
      if (this.epoch !== epoch) return 'stopped';
    }
    return 'ended';
  }

  stop() {
    this.epoch++;
    this.halt();
  }

  /** Where the playing clip is, in seconds (a story's clock), or null when nothing plays. */
  position(): number | null {
    return this.finish && this.el ? this.el.currentTime : null;
  }

  /** Changes the speed of what is playing, without restarting it. */
  setRate(rate: number) {
    if (!this.el) return;
    this.el.defaultPlaybackRate = rate;
    this.el.playbackRate = rate;
  }

  private primed = false;

  /**
   * Call from a tap (Start, record): plays a moment of silence so that iOS lets this element play
   * later without a tap, for example the model and your recording after recording stops.
   */
  prime() {
    if (this.primed || this.finish) return;
    const el = this.element();
    this.primed = true;
    el.src = SILENCE;
    el.play()
      .then(() => el.pause())
      .catch(() => {
        this.primed = false;
      });
  }

  /** Loads clips into memory ahead of time so they start at once. */
  warm(srcs: string[]) {
    for (const src of srcs) bank.load(src).catch(() => undefined);
  }

  /** Loads a clip into memory; once it resolves, play() starts the clip within the tap that asks. */
  async load(src: string): Promise<void> {
    await bank.load(src);
  }
}

export const player = new Player();
